import dayjs from './dayjs';

const dateOnlyRe = /^(\d{4})-(\d{2})-(\d{2})$/;

// Date-only ISO (YYYY-MM-DD) → UTC-полночь. Иначе new Date("2025-04-14") даёт local-полночь,
// и JSON.stringify (через toISOString) сдвигает дату на день назад в +TZ окружениях
// (например MSK: 2025-04-14 local → 2025-04-13T21:00:00Z).
const parseDateOnly = (val: string): Date | null => {
    const m = val.match(dateOnlyRe);
    if (!m) return null;
    const year = Number(m[1]);
    const month = Number(m[2]) - 1;
    const day = Number(m[3]);
    const d = new Date(Date.UTC(year, month, day));
    // Date.UTC нормализует overflow (2025-02-31 → 03 март) — отвергаем, а не сдвигаем
    // (паритет со строгим parseRuLoose ниже и с прежним date-fns parseISO).
    if (d.getUTCFullYear() !== year || d.getUTCMonth() !== month || d.getUTCDate() !== day) return null;
    return d;
};

// RU-формат от бэкенда: DD.MM.YYYY[ HH:mm[:ss]]. С сервера приходят значения
// templateDefaultValue (например {startOfPastWeeks}) уже подставленные именно так.
// Если время не задано — возвращаем UTC midnight (паритет с parseDateOnly:
// JSON.stringify через toISOString не сдвинет день в +TZ окружениях).
// Если время есть — local datetime: серверное время трактуется как локальное.
const ruDateRe = /^(\d{1,2})[.\-/](\d{1,2})[.\-/](\d{4}|\d{2})(?:[ T](\d{1,2}):(\d{1,2})(?::(\d{1,2}))?)?$/;

const parseRuLoose = (val: string): Date | null => {
    const m = val.trim().match(ruDateRe);
    if (!m) return null;
    const day = Number(m[1]);
    const month = Number(m[2]) - 1;
    let year = Number(m[3]);
    if (m[3].length === 2) year += 2000;
    if (day < 1 || day > 31 || month < 0 || month > 11 || year < 1900 || year > 2999) return null;

    if (m[4] === undefined) {
        const d = new Date(Date.UTC(year, month, day));
        if (d.getUTCFullYear() !== year || d.getUTCMonth() !== month || d.getUTCDate() !== day) return null;
        return d;
    }

    const hh = Number(m[4]);
    const mm = Number(m[5]);
    const ss = Number(m[6] ?? 0);
    if (hh > 23 || mm > 59 || ss > 59) return null;
    const d = new Date(year, month, day, hh, mm, ss);
    if (d.getFullYear() !== year || d.getMonth() !== month || d.getDate() !== day) return null;
    return d;
};

// ISO-datetime YYYY-MM-DDTHH:mm[:ss[.sss]] с опциональной зоной (Z | ±HH:mm).
// dayjs(val) без формата тихо нормализует переполнение (2025-02-31 → 03 март,
// 99:00 → +день) — в отличие от date-fns parseISO, который отдавал Invalid Date.
// Поэтому валидируем календарные поля «как написано» (диапазоны + день в месяце),
// а сборку — включая применение зоны/мс — отдаём dayjs. Зону при валидации
// игнорируем: на допустимость дня/времени она не влияет.
const isoDateTimeRe = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})?$/;

const parseIsoDateTime = (val: string): Date | null => {
    const m = val.match(isoDateTimeRe);
    if (!m) return null;
    const year = Number(m[1]);
    const month = Number(m[2]) - 1;
    const day = Number(m[3]);
    const hh = Number(m[4]);
    const mm = Number(m[5]);
    const ss = Number(m[6] ?? 0);
    if (month < 0 || month > 11 || hh > 23 || mm > 59 || ss > 59) return null;
    const probe = new Date(Date.UTC(year, month, day));
    if (probe.getUTCFullYear() !== year || probe.getUTCMonth() !== month || probe.getUTCDate() !== day) {
        return null; // 2025-02-31 и подобное
    }
    const d = dayjs(val);
    return d.isValid() ? d.toDate() : null;
};

const safeDate = (val: unknown): Date | null => {
    if (val == null) return null;
    if (val instanceof Date) {
        return Number.isNaN(val.getTime()) ? null : val;
    }
    if (typeof val === 'string') {
        if (val === '') return null;
        // Дата-only ISO-форму отдаём строго parseDateOnly и коммитим результат:
        // на overflow вернётся null, и мы НЕ проваливаемся в финальный new Date(val),
        // который '2025-02-31' молча сдвинул бы на 03 март (V8 нормализует).
        if (dateOnlyRe.test(val)) return parseDateOnly(val);
        const ruParsed = parseRuLoose(val);
        if (ruParsed) return ruParsed;
        // Числовая строка — timestamp (ms). Field.tsx раньше делал new Date(+val);
        // useFormulaReplace для OFFSETDAY и подобных возвращает timestamp как строку.
        if (/^-?\d+$/.test(val)) {
            const n = Number(val);
            if (Number.isFinite(n)) {
                const d = new Date(n);
                if (!Number.isNaN(d.getTime())) return d;
            }
        }
        if (val.includes('T')) return parseIsoDateTime(val);
        const d = new Date(val);
        return Number.isNaN(d.getTime()) ? null : d;
    }
    if (typeof val === 'number' && Number.isFinite(val)) {
        const d = new Date(val);
        return Number.isNaN(d.getTime()) ? null : d;
    }
    return null;
};

export const toDateOrNull = (val: unknown): Date | null => safeDate(val);

export const dateToIsoDate = (val: unknown): string | null => {
    const d = safeDate(val);
    return d ? dayjs(d).format('YYYY-MM-DD') : null;
};

export const dateToIsoDateTime = (val: unknown): string | null => {
    const d = safeDate(val);
    return d ? dayjs(d).format('YYYY-MM-DD[T]HH:mm:ss') : null;
};

export const isoToDate = (val: string | null | undefined): Date | null => {
    if (!val) return null;
    return safeDate(val);
};

export const yearToIsoDate = (val: number | string | null | undefined): string | null => {
    if (val == null || val === '') return null;
    const n = typeof val === 'number' ? val : Number(val);
    if (!Number.isFinite(n)) return null;
    return `${n}-01-01`;
};

export const isoDateToYear = (val: string | null | undefined): number | null => {
    if (!val) return null;
    const year = Number(val.slice(0, 4));
    return Number.isFinite(year) ? year : null;
};

/**
 * ISO date (YYYY-MM-DD[...]) → RU-формат DD.MM.YYYY для отображения в масочном поле.
 * Чисто строковая операция — без Date, чтобы не ловить TZ-сдвиги. Пустое/невалидное → ''.
 */
export const isoDateToRu = (iso: string | null | undefined): string => {
    if (!iso) return '';
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
    return m ? `${m[3]}.${m[2]}.${m[1]}` : '';
};

/**
 * Парсер русского формата DD.MM.YYYY (или DD.MM.YY → 20YY) в ISO YYYY-MM-DD.
 * Поддерживает разделители ., -, /. Принимает и форматы из календаря (ISO YYYY-MM-DD),
 * возвращая дату без хвоста. Используется как dateParser у Mantine DateInput —
 * возвращаемая строка идёт в onChange. Границы 1900–2999 — деловой диапазон документов
 * (даты вне исторически не встречаются и почти всегда означают опечатку).
 */
export const parseRuDate = (input: string): string | null => {
    if (!input) return null;
    const trimmed = input.trim();
    if (!trimmed) return null;

    // Уже ISO — обрезаем хвост (время) и пропускаем.
    const isoMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoMatch) return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;

    // DD.MM.YYYY | DD/MM/YYYY | DD-MM-YYYY (с опциональным временем — игнорируем хвост).
    // Порядок \d{4}|\d{2} важен: regex greedy выбирает первое совпадение,
    // \d{2} съело бы первые 2 цифры от четырёхзначного года.
    const m = trimmed.match(/^(\d{1,2})[.\-/](\d{1,2})[.\-/](\d{4}|\d{2})(?!\d)/);
    if (!m) return null;

    const day = Number(m[1]);
    const month = Number(m[2]);
    let year = Number(m[3]);
    if (m[3].length === 2) year += 2000;

    if (day < 1 || day > 31 || month < 1 || month > 12 || year < 1900 || year > 2999) return null;

    const probe = new Date(year, month - 1, day);
    if (probe.getFullYear() !== year || probe.getMonth() !== month - 1 || probe.getDate() !== day) {
        return null; // 31.02 и подобное
    }

    return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
};
